import * as React from 'react'
import Moveable from 'react-moveable'
import { DELEGATE, FlowObjects, MARK_ALL_INACTIVE, SET_CURRENT_OBJECT, SET_POSITION, SET_ROTATION, SET_SIZE, SET_SIZE_AND_POSITION, SET_INTERACTING } from '../fsm/types'
import { Send } from '../fsm/useMachine'
import { applyRatio, unapplyRatio } from './helpers'
import { interactionState } from './update-listener'

interface MMOInteractivityOptions {
    mmo: FlowObjects
    display(props: MMOOptions): React.ReactNode
    allowedWhenActive: {
        resizable: boolean,
        draggable: boolean,
        rotatable: boolean,
        keepRatio: boolean,
    }
    size: { width: number, height: number, ratio: number }
    isCurrentObject: boolean
    hasAnySelection: boolean  // True if any object is selected (disables hover preview on other objects)
    send: Send
    dragging: [boolean, React.Dispatch<React.SetStateAction<boolean>>]
}

export interface MMOOptions {
    mmo: FlowObjects
    size: { width: number, height: number, ratio: number }
    onClick(e: React.MouseEvent<HTMLElement | SVGSVGElement>): void
    onMouseEnter(e: React.MouseEvent<HTMLElement | SVGSVGElement>): void
    onMouseLeave(e: React.MouseEvent<HTMLElement | SVGSVGElement>): void
    forwardedRef: React.RefObject<any>
    setShowTransformer: React.Dispatch<React.SetStateAction<boolean>>
}

export const MMOInteractivity: React.FC<MMOInteractivityOptions> = (props) => {
    const { dragging, isCurrentObject, hasAnySelection, size, allowedWhenActive, mmo, display, send } = props
    const [target, setTarget] = React.useState<HTMLElement>();
    const [functionality, setFunctionality] = React.useState({
        resizable: false,
        draggable: false,
        rotatable: false,
        keepRatio: false,
    })
    const childRef = React.useRef<HTMLElement>()
    const [isDragging, setDragging] = dragging
    const [showTransformer, setShowTransformer] = React.useState(isCurrentObject)
    const scaledObjects = applyRatio({
        left: mmo.frame.x,
        top: mmo.frame.y,
        width: mmo.frame.width,
        height: mmo.frame.height,
    }, props.size.ratio)

    const [frame, setFrame] = React.useState({
        // X,Y translation
        translate: [0, 0],
        rotate: mmo.rotation * 90,
        width: scaledObjects.width,
        height: scaledObjects.height,
        // Transform relative to origin
        transformOrigin: "50% 50%",
    });

    React.useEffect(() => {
        // @ts-ignore
        setTarget(document.getElementById(mmo.id.replace(/-/g, "") as HTMLElement));
    }, [isCurrentObject]);

    React.useEffect(() => {
        setShowTransformer(isCurrentObject)
        if (isCurrentObject) {
            setFunctionality(allowedWhenActive)
        } else {
            setFunctionality({
                resizable: false,
                draggable: false,
                rotatable: false,
                keepRatio: false,
            })
        }
    }, [isCurrentObject])

    return (
        <React.Fragment>
            {document.querySelector(".container") && showTransformer &&
                <Moveable
                    target={childRef.current || target}
                    container={document.querySelector(".container")! as HTMLElement}
                    portalContainer={document.querySelector(".editor-artboard") as HTMLElement || document.querySelector(".container")! as HTMLElement}
                    snappable={true}
                    // No bounds - allow objects to extend beyond canvas (bleed)
                    elementGuidelines={Array.from(document.getElementsByClassName("mmo"))}
                    throttleResize={0}
                    renderDirections={["nw", "n", "ne", "w", "e", "sw", "s", "se"]}
                    edge={true}
                    zoom={1}
                    rotationPosition={"top"}
                    origin={false}
                    snapCenter={true}
                    originDraggable={true}
                    originRelative={true}
                    throttleDrag={0}
                    padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
                    throttleRotate={90}
                    onResizeStart={(e) => {
                        interactionState.isInteracting = true; // Set synchronously first!
                        send({ type: SET_INTERACTING, value: true });
                        e.setOrigin(["%", "%"]);
                        e.dragStart && e.dragStart.set(frame.translate);
                    }}
                    onResize={(e) => {
                        const beforeTranslate = e.drag.beforeTranslate;
                        frame.translate = beforeTranslate;
                        e.target.style.width = `${e.width}px`;
                        e.target.style.height = `${e.height}px`;
                        e.target.style.transform = `translate(${beforeTranslate[0]}px, ${beforeTranslate[1]}px) rotate(${frame.rotate}deg)`;
                        setFrame((val) => ({
                            ...val,
                            translate: [beforeTranslate[0], beforeTranslate[1]],
                            width: e.width,
                            height: e.height
                        }))
                    }}
                    onResizeEnd={(e) => {
                        // Calculate new size and position
                        const newSize = unapplyRatio({ width: frame.width, height: frame.height }, size.ratio)

                        // If resize moved the object (from non-bottom-right handles), batch size and position update
                        if (frame.translate[0] !== 0 || frame.translate[1] !== 0) {
                            const vals = unapplyRatio({
                                x: frame.translate[0],
                                y: frame.translate[1]
                            }, size.ratio)

                            // Send batched update: one event for both size and position = one COMMIT
                            send({
                                type: DELEGATE,
                                payload: {
                                    type: SET_SIZE_AND_POSITION,
                                    width: newSize.width,
                                    height: newSize.height,
                                    x: mmo.frame.x + vals.x,
                                    y: mmo.frame.y + vals.y
                                },
                                id: mmo.id
                            })
                        } else {
                            // Only size changed, send size update only
                            send({
                                type: DELEGATE,
                                payload: {
                                    type: SET_SIZE,
                                    width: newSize.width,
                                    height: newSize.height
                                },
                                id: mmo.id
                            })
                        }

                        setFrame((val) => ({
                            ...val,
                            translate: [0, 0],
                            width: scaledObjects.width,
                            height: scaledObjects.height,
                        }))

                        // Clear inline transform to prevent it from affecting next drag
                        if (target) {
                            target.style.transform = `rotate(${frame.rotate}deg)`
                        }

                        // Clear interaction flag after a brief delay
                        // Now that we use ignoreElements, we don't need a long delay
                        setTimeout(() => {
                            interactionState.isInteracting = false; // Clear synchronously
                            send({ type: SET_INTERACTING, value: false });
                        }, 100);
                    }}
                    onDragOriginStart={(e) => {
                        e.dragStart && e.dragStart.set(frame.translate);
                    }}
                    onDragOrigin={(e) => {
                        frame.translate = e.drag.beforeTranslate;
                        frame.transformOrigin = e.transformOrigin;
                    }}
                    onDragStart={(e) => {
                        interactionState.isInteracting = true; // Set synchronously first!
                        send({ type: SET_INTERACTING, value: true });
                        setDragging(true)
                    }}
                    onDrag={(e) => {
                        setFrame((val) => ({
                            ...val,
                            translate: e.beforeTranslate,
                            transformOrigin: "50% 50%"
                        }))
                        if (target) target.style.transform = e.transform;
                    }}
                    onDragEnd={() => {
                        setDragging(false)
                        const vals = unapplyRatio({
                            x: frame.translate[0],
                            y: frame.translate[1]
                        }, size.ratio)
                        console.info(`[onDragEnd] Translate values:`, vals)
                        console.info(`[onDragEnd] Current mmo.frame:`, mmo.frame)
                        console.info(`[onDragEnd] size.ratio:`, size.ratio)
                        const newX = vals.x + mmo.frame.x
                        const newY = vals.y + mmo.frame.y
                        console.info(`[onDragEnd] Calculated new position: x=${newX}, y=${newY}`)
                        send({
                            type: DELEGATE,
                            payload: {
                                type: SET_POSITION,
                                x: newX,
                                y: newY
                            },
                            id: mmo.id
                        })
                        setFrame((val) => ({
                            ...val,
                            translate: [0, 0],
                        }))
                        if (target) target.style.transform = `rotate(${frame.rotate}deg)`

                        // Delay clearing interaction flag to allow preview generation
                        // Now that we use ignoreElements, we don't need a long delay
                        setTimeout(() => {
                            interactionState.isInteracting = false; // Clear synchronously
                            send({ type: SET_INTERACTING, value: false });
                        }, 100);
                    }}
                    onRotateStart={(e) => {
                        interactionState.isInteracting = true; // Set synchronously first!
                        send({ type: SET_INTERACTING, value: true });
                        setDragging(n=>true)
                        e.set(frame.rotate)
                    }}
                    onRotate={(e) => {
                        setFrame((val) => ({
                            ...val,
                            rotate: e.beforeRotate
                        }))
                        if (target) target.style.transform = `rotate(${e.beforeRotate}deg)`
                    }}
                    onRotateEnd={() => {
                        send({
                            type: DELEGATE,
                            payload: {
                                type: SET_ROTATION,
                                rotation: degreeToMultiple90(frame.rotate)
                            },
                            id: mmo.id
                        })
                        setFrame((val) => ({
                            ...val,
                            rotate: frame.rotate,
                        }))

                        // Delay clearing interaction flag to allow preview generation
                        // Now that we use ignoreElements, we don't need a long delay
                        setTimeout(() => {
                            interactionState.isInteracting = false; // Clear synchronously
                            send({ type: SET_INTERACTING, value: false });
                        }, 100);
                    }}
                    {...functionality}
                />}
            {display({
                mmo,
                size,
                forwardedRef: childRef,
                onMouseEnter: () => !isCurrentObject && !isDragging && !hasAnySelection && setShowTransformer(true),
                onMouseLeave: () => {
                    if (!isCurrentObject && !isDragging) setShowTransformer(false)
                },
                onClick: (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!isCurrentObject) {
                        setFunctionality(allowedWhenActive)
                        send({ type: SET_CURRENT_OBJECT, id: mmo.id })
                        setShowTransformer(true)
                    }
                },
                setShowTransformer,
            })}
        </React.Fragment>
    )
}

const degreeToMultiple90 = (rotation: number) => {
    const as0To3 = Math.trunc(rotation / 90)
    if (as0To3 >= 1) {
        return as0To3
    } else {
        console.log(as0To3)
        return 4 - as0To3
    }
}